import { useState } from "react";
import { useTranslation } from "react-i18next";
import { HardDrive } from "lucide-react";
import { api } from "@/lib/api";

// Client-side identifiers (safe to embed, restricted by domain in Google Cloud).
// When either is absent the button is inert (renders nothing), so the feature
// ships dormant and activates once the two build vars are set.
const API_KEY = import.meta.env.VITE_GOOGLE_API_KEY as string | undefined;
const CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID as string | undefined;
const DRIVE_SCOPE = "https://www.googleapis.com/auth/drive.file";

// Cloudinary-servable types (must match the server /upload allow-list).
const SUPPORTED = new Set([
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "image/jpeg",
  "image/png",
  "image/heic",
  "image/webp",
]);

function loadScript(src: string): Promise<void> {
  return new Promise((resolve, reject) => {
    if (document.querySelector(`script[src="${src}"]`)) return resolve();
    const s = document.createElement("script");
    s.src = src;
    s.async = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error(`Failed to load ${src}`));
    document.body.appendChild(s);
  });
}

let pickerLoaded = false;
async function ensureLibraries(): Promise<void> {
  await Promise.all([
    loadScript("https://apis.google.com/js/api.js"),
    loadScript("https://accounts.google.com/gsi/client"),
  ]);
  if (!pickerLoaded) {
    await new Promise<void>((resolve) => (window as any).gapi.load("picker", () => resolve()));
    pickerLoaded = true;
  }
}

interface Props {
  folder?: string;
  onUploaded: (url: string) => void;
}

/** "Choose from Google Drive" — picks a file, copies its bytes into Cloudinary. */
export function GoogleDrivePicker({ folder = "costadorada1/documents", onUploaded }: Props) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!API_KEY || !CLIENT_ID) return null; // inert until configured

  async function ingest(fileId: string, name: string, mimeType: string, token: string) {
    setBusy(true);
    setError(null);
    try {
      if (!SUPPORTED.has(mimeType)) {
        throw new Error(t("legal.driveUnsupported"));
      }
      const res = await fetch(`https://www.googleapis.com/drive/v3/files/${fileId}?alt=media`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(t("legal.driveDownloadFailed"));
      const blob = await res.blob();
      const file = new File([blob], name, { type: mimeType });
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/upload?folder=${folder}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onUploaded(data.url);
    } catch (e: any) {
      setError(e?.response?.data?.message ?? e?.message ?? t("common.error"));
    } finally {
      setBusy(false);
    }
  }

  async function openPicker() {
    setError(null);
    try {
      await ensureLibraries();
      const tokenClient = (window as any).google.accounts.oauth2.initTokenClient({
        client_id: CLIENT_ID,
        scope: DRIVE_SCOPE,
        callback: (resp: any) => {
          if (resp.error) {
            setError(t("common.error"));
            return;
          }
          const token = resp.access_token;
          const picker = new (window as any).google.picker.PickerBuilder()
            .addView((window as any).google.picker.ViewId.DOCS)
            .setOAuthToken(token)
            .setDeveloperKey(API_KEY)
            .setCallback((data: any) => {
              if (data.action === (window as any).google.picker.Action.PICKED) {
                const doc = data.docs[0];
                ingest(doc.id, doc.name, doc.mimeType, token);
              }
            })
            .build();
          picker.setVisible(true);
        },
      });
      tokenClient.requestAccessToken({ prompt: "" });
    } catch (e: any) {
      setError(e?.message ?? t("common.error"));
    }
  }

  return (
    <div>
      <button type="button" className="btn-secondary" disabled={busy} onClick={openPicker}>
        <HardDrive size={14} /> {busy ? t("legal.uploading") : t("legal.chooseFromDrive")}
      </button>
      {error && <p className="text-xs text-red-600 mt-1">{error}</p>}
    </div>
  );
}
